import React, { useEffect, useMemo, useState } from 'react';
import { Input, Autocomplete, AutocompleteItem, Button, Chip } from '@heroui/react';
import { FileText } from 'lucide-react';
import { AppDatePicker } from '../../resources/components/AppDatePicker.js';
import { fieldsForType } from '../documentFields.js';
import { toPickerValue, expiryState } from '../documentDates.js';
import { formatUserDate } from '../formatUserDate.js';
import { toAttachmentList } from '../attachments.js';
import AttachmentList from './AttachmentList.js';
import CredentialDrawer, { PreviewRow } from './CredentialDrawer.js';

/**
 * DocumentDialog — add / edit one document.
 *
 * A centered modal rather than a side drawer (USER): the form is short and
 * type-driven, so a sheet sliding in from the edge gave it more room than it
 * needs and read as a heavier surface than the action deserves.
 *
 * The form is type-driven: `fieldsForType` decides which inputs exist, exactly
 * as the legacy `ng-if="fields[document.type].…"` did. Changing the type
 * rewrites the form, and a value belonging to a field the new type does not
 * have is dropped on save rather than sent as a stray column.
 *
 * Dates use the shared `AppDatePicker`, so they render in the format the user
 * chose (`userSetting.angularDateFormat`) instead of the browser's locale.
 * Where a type also carries a time (insurance), the picker switches to minute
 * granularity rather than growing a second control — which is what keeps the
 * two dates on one row.
 */
const BLANK = {
	id: null,
	name: '',
	type: '',
	number: '',
	insuranceCompany: '',
	doctorsName: '',
	dateOfIssue: null,
	dateExpires: null
};

/** `HH:MM` out of a stored `H:i:s`, or ''. */
const hhmm = t => (/^\d{1,2}:\d{2}/.exec(String(t || '')) || [''])[0];

/**
 * DocumentPreview — the record as stored, in the legacy preview's field order
 * (`views/users/tpl-documents-preview.twig`): only the fields its type has.
 */
function DocumentPreview({ doc, typeName, attachments }) {
	const f = fieldsForType(doc.type);
	const exp = expiryState(doc.dateExpires);
	const issued = formatUserDate(doc.dateOfIssue);
	const expires = formatUserDate(doc.dateExpires);
	return (
		<div className="flex flex-col gap-5">
			<dl className="flex flex-col">
				<PreviewRow label="Document Type">{typeName(doc.type) || '—'}</PreviewRow>
				{f.insuranceCompany ? (
					<PreviewRow label="Insurance Company">{doc.insuranceCompany || '—'}</PreviewRow>
				) : null}
				{f.doctorsName ? <PreviewRow label="Doctor's Name">{doc.doctorsName || '—'}</PreviewRow> : null}
				{f.documentNumber || f.certificateNumber ? (
					<PreviewRow label={f.certificateNumber ? 'Certificate Number' : 'Document Number'}>
						{doc.number || '—'}
					</PreviewRow>
				) : null}
				{f.dateOfIssue ? (
					<PreviewRow label={String(doc.type) === '8' ? 'Effective Date' : 'Date of Issue'}>
						{issued ? `${issued}${f.timeOfIssue && hhmm(doc.timeOfIssue) ? ` ${hhmm(doc.timeOfIssue)}` : ''}` : '—'}
					</PreviewRow>
				) : null}
				<PreviewRow label="Expiration Date">
					{expires ? (
						<span className="inline-flex flex-wrap items-center gap-2">
							{`${expires}${f.timeExpires && hhmm(doc.timeExpires) ? ` ${hhmm(doc.timeExpires)}` : ''}`}
							{exp ? (
								<Chip size="sm" variant="flat" color={exp.tone}>
									{exp.label}
								</Chip>
							) : null}
						</span>
					) : (
						<span className="text-default-400">No expiration</span>
					)}
				</PreviewRow>
			</dl>

			<div className="flex flex-col gap-2">
				<span className="text-[13px] font-semibold text-foreground">Scans</span>
				<AttachmentList items={attachments} />
			</div>
		</div>
	);
}

function DocumentDialog({
	isOpen,
	onClose,
	document: doc,
	documentTypes,
	onSave,
	saving,
	onUpload,
	onDeleteAsset,
	uploading,
	mode = 'edit',
	onModeChange,
	onDelete,
	deleting = false
}) {
	const [form, setForm] = useState(BLANK);

	useEffect(() => {
		if (!isOpen) return;
		if (!doc) {
			setForm(BLANK);
			return;
		}
		setForm({
			id: doc.id ?? null,
			name: doc.name || '',
			type: doc.type == null ? '' : String(doc.type),
			number: doc.number || '',
			insuranceCompany: doc.insuranceCompany || '',
			doctorsName: doc.doctorsName || '',
			// Renters insurance keeps its times in separate columns.
			dateOfIssue: toPickerValue(doc.dateOfIssue, doc.timeOfIssue),
			dateExpires: toPickerValue(doc.dateExpires, doc.timeExpires)
		});
		// Re-seeded on open, on another record and on every mode switch (so Cancel
		// drops unsaved edits) — NOT when the same record is re-read after a scan
		// upload, which would wipe what is being typed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, doc && doc.id, mode]);

	const fields = useMemo(() => fieldsForType(form.type), [form.type]);

	// Existing scans, from the presigned `attachments[]` the list carries.
	const attachments = useMemo(() => toAttachmentList(doc), [doc]);
	const set = (k, v) => setForm(prev => (prev[k] === v ? prev : { ...prev, [k]: v }));
	const typeName = id =>
		((documentTypes || []).find(t => String(t.id) === String(id)) || {}).name || '';

	const numberLabel = fields.certificateNumber ? 'Certificate Number' : 'Document Number';
	const canSave = Boolean(form.name.trim() && form.type);
	const showIssue = Boolean(fields.dateOfIssue);
	const showExpires = Boolean(fields.dateExpires || fields.dateExpiresTSA);

	const control = {
		variant: 'bordered',
		labelPlacement: 'outside',
		radius: 'sm',
		classNames: { inputWrapper: 'h-10 min-h-10', trigger: 'h-10 min-h-10' }
	};

	const viewing = mode === 'view' && doc;

	return (
		<CredentialDrawer
			isOpen={isOpen}
			onClose={onClose}
			icon={FileText}
			title={viewing ? doc.name || 'Document' : form.id ? 'Edit Document' : 'Add Document'}
			subtitle={
				viewing
					? typeName(doc.type)
					: 'Fields below follow the selected document type.'
			}
			mode={viewing ? 'view' : 'edit'}
			onEdit={onModeChange ? () => onModeChange('edit') : undefined}
			onDelete={onDelete ? () => onDelete(doc) : undefined}
			deleting={deleting}
			footer={
				viewing ? null : (
					<>
						<Button
							variant="bordered"
							radius="sm"
							onPress={() => (form.id && onModeChange ? onModeChange('view') : onClose())}
						>
							Cancel
						</Button>
						<Button
							color="primary"
							radius="sm"
							isDisabled={!canSave}
							isLoading={saving}
							onPress={() => onSave(form, fields)}
						>
							{form.id ? 'Save Document' : 'Add Document'}
						</Button>
					</>
				)
			}
		>
			{viewing ? (
				<DocumentPreview doc={doc} typeName={typeName} attachments={attachments} />
			) : (
				<>
					<Input
						{...control}
						label="Document Name"
						isRequired
						value={form.name}
						onValueChange={v => set('name', v)}
						placeholder="e.g. My medical reference"
					/>

					{/* Searchable: the type list is long enough that scanning it is
					    slower than typing three letters of it. */}
					<Autocomplete
						{...control}
						label="Document Type"
						isRequired
						defaultItems={documentTypes}
						selectedKey={form.type ? String(form.type) : null}
						onSelectionChange={key => set('type', key == null ? '' : String(key))}
						placeholder="Search document types"
						menuTrigger="input"
					>
						{t => <AutocompleteItem key={String(t.id)}>{t.name}</AutocompleteItem>}
					</Autocomplete>

					{(fields.documentNumber || fields.certificateNumber) && (
						<Input
							{...control}
							label={numberLabel}
							value={form.number}
							onValueChange={v => set('number', v)}
						/>
					)}

					{fields.doctorsName && (
						<Input
							{...control}
							label="Doctor's Name"
							value={form.doctorsName}
							onValueChange={v => set('doctorsName', v)}
						/>
					)}

					{fields.insuranceCompany && (
						<Input
							{...control}
							label="Insurance Company"
							value={form.insuranceCompany}
							onValueChange={v => set('insuranceCompany', v)}
						/>
					)}

					{(showIssue || showExpires) && (
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							{showIssue && (
								<AppDatePicker
									label="Date of Issue"
									variant="bordered"
									labelPlacement="outside"
									radius="sm"
									granularity={fields.timeOfIssue ? 'minute' : 'day'}
									value={form.dateOfIssue}
									onChange={v => set('dateOfIssue', v)}
								/>
							)}
							{showExpires && (
								<AppDatePicker
									label="Expiration"
									variant="bordered"
									labelPlacement="outside"
									radius="sm"
									granularity={fields.timeExpires ? 'minute' : 'day'}
									value={form.dateExpires}
									onChange={v => set('dateExpires', v)}
								/>
							)}
						</div>
					)}

					{/* Attachments hang off the document id, so they can only be
					    added once it exists. Before the first save this says so
					    rather than showing a control that would fail. */}
					{form.id ? (
						<div className="flex flex-col gap-2 pt-1">
							<span className="text-[13px] font-medium text-default-700">Attachments</span>
							<AttachmentList
								items={attachments}
								multiple
								onUpload={files => onUpload(form.id, files)}
								onDelete={item => onDeleteAsset(form.id, item)}
								uploading={uploading}
							/>
						</div>
					) : (
						<p className="text-[12.5px] text-default-500 m-0">
							Save the document first — attachments can be added to it afterwards.
						</p>
					)}
				</>
			)}
		</CredentialDrawer>
	);
}

export default DocumentDialog;
